import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { map, take } from 'rxjs';
import { DialogRef } from '@angular/cdk/dialog';

import { RpcInputOptions } from '@core-controls/components/rpc-input/models/rpc-input-options';
import { RpcSelectControltOptions } from '@core-controls/components/rpc-select/models/rpc-select-controlt-options';
import { RpcSelectOption } from '@core-controls/components/rpc-select/models/rpc-select-option';
import { ValidatorsMapItem } from '@core-controls/models/validators-map-item';
import { SimpleChanges } from '@core-models/utilities/generic-simple-changes';
import { ValidationService } from '@core-validation/validation.service';
import { Folder } from '@folders/models/folder';
import { SavedSearch } from '@saved-search/models/saved-search';
import { SavedSearchForm } from '../../models/saved-search-form';
import { IManageFolderModel } from '@folders/models/manage-folder-model';
import { FoldersStoreWriteService } from '@folders/store/services/folders-store-write.service';
import { GoogleAnalyticsStoreService } from '@core-layout/app/store/services/google-analytics-store.service';
import { MatchMediaService } from '@media/services/match-media.service';
import { RpcCheckboxState } from '@core-controls/components/rpc-checkbox/types/rpc-checkbox-state';
import { RpcDialogService } from '@core-services/dialog/rpc-dialog.service';
import { AppConfigurationService } from '@app-config/services/app-configuration.service';
import { Observable } from '@apollo/client';
import { RpcInputComponent } from '../../../core-modules/controls/components/rpc-input/rpc-input.component';
import { RpcSelectComponent } from '../../../core-modules/controls/components/rpc-select/rpc-select.component';
import { AddEditFolderFormComponent } from '../../../listing-modules/folders/components/add-edit-folder-form/add-edit-folder-form.component';
import { NgTemplateOutlet, AsyncPipe } from '@angular/common';
import { RpcCheckboxComponent } from '../../../core-modules/controls/components/rpc-checkbox/rpc-checkbox.component';
import { RpcButtonComponent } from '../../../core-modules/controls/components/rpc-button/rpc-button.component';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'saved-search-form',
    templateUrl: './saved-search-form.component.html',
    styleUrls: ['./saved-search-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RpcInputComponent, RpcSelectComponent, AddEditFolderFormComponent, NgTemplateOutlet, RpcCheckboxComponent, RpcButtonComponent, AsyncPipe, TranslateModule]
})
export class SavedSearchFormComponent implements OnInit, OnChanges {
    @Input() public savedSearch: SavedSearch | null = null;
    @Input() public savedSearches: SavedSearch[] = [];
    @Input() public folders: Folder[] = [];
    @Input() public folderId: number | null = null;
    @Input() public isRentalsSearchOption = false;

    @ViewChild('mobileFolderList') mobileFolderList: TemplateRef<unknown>;
    @ViewChild('addEditFolderForm') addEditFolderForm: TemplateRef<unknown>;

    public form: UntypedFormGroup;
    public nameControlOptions: RpcInputOptions;
    public folderControlOptions: RpcSelectControltOptions;
    public includePreviousMatches?: RpcCheckboxState;
    public enableEmail: RpcCheckboxState = 'unchecked';
    public isCreateFolderFormVisible = false;
    public readonly defaultNewSavedSearchId = Math.floor(Math.random() * -99999);
    public readonly isMobile$ = this.matchMediaService.onMediaChange.pipe(map(mediaQuery => mediaQuery === 'xs'));
    public readonly isDisabledByTenantAgreementPolicy$ = this.configurationService.configuration$
        .pipe(map(configuration => !configuration.company.isTenantAgreementPolicyEnforced && this.isRentalsSearchOption));

    public readonly folderNotSelectedId = -1;
    private dialogRef: DialogRef;
    private newlyCreatedFolderName: string;

    constructor(
        private readonly validationService: ValidationService,
        private readonly formBuilder: UntypedFormBuilder,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly foldersStoreWriteService: FoldersStoreWriteService,
        private readonly googleAnalyticsStoreService: GoogleAnalyticsStoreService,
        private readonly matchMediaService: MatchMediaService,
        private readonly rpcDialogService: RpcDialogService,
        private readonly configurationService: AppConfigurationService,
    ) { }

    public get isShowIncludePreviousMatchesCheckbox(): boolean {
        const folderId = this.folderControlValue;

        return this.savedSearch != null && folderId != null && folderId !== this.folderNotSelectedId;
    }

    public get folderControlValue(): number {
        return this.form.get('folder').value as number;
    }

    public get nameControlValue(): string {
        return this.form.get('name').value as string;
    }

    public ngOnInit(): void {
        this.buildForm();
    }

    public ngOnChanges(changes: SimpleChanges<SavedSearchFormComponent>): void {
        this.recreateFormControls();
    }

    public onCreateFolderClicked(model: IManageFolderModel): void {
        this.onCloseCreateFolderControl();

        this.foldersStoreWriteService.create(model.name);

        this.googleAnalyticsStoreService.addCreateFolderEvent('saved_search');
    }

    public getFormValue(): SavedSearchForm {
        const folderId = this.folderControlValue !== this.folderNotSelectedId ? this.folderControlValue : null;

        return {
            id: this.savedSearch?.id ?? this.defaultNewSavedSearchId,
            name: this.nameControlValue,
            folderId,
            includePreviousMatches: folderId != null && this.includePreviousMatches === 'checked',
            enableEmail: this.enableEmail === 'checked'
        };
    }

    public onEnableEmailChange(value: RpcCheckboxState) {
        this.enableEmail = value;
    }

    public getNewFolderName(): string {
        let folderName = this.nameControlValue;

        if (folderName == null) {
            return null;
        }

        while (this.folders.some(({ name }) => name === folderName)) {
            folderName += '1';
        }

        return folderName;
    }

    public onCloseCreateFolderControl(): void {
        this.isCreateFolderFormVisible = false;
    }

    public onShowCreateFolderControl(): void {
        if (this.matchMediaService.activeMediaQuery === 'xs') {
            this.dialogRef.close();

            const actionOnSuccess = (model: IManageFolderModel) => this.newlyCreatedFolderName = model.name;

            this.foldersStoreWriteService.showAddEditFolderDialog({ name: this.getNewFolderName(), eventSource: 'saved_search', actionOnSuccess });
        } else {
            this.isCreateFolderFormVisible = true;
        }
    }

    public onFolderMobileClick(): void {
        if (this.matchMediaService.activeMediaQuery === 'xs') {
            this.dialogRef = this.rpcDialogService.openBottomSheet(this.mobileFolderList, null, { customClass: ['saved-search-mobile-options-wrap', 'bottom-panel-wrap'] });
        }
    }

    public onCloseFolderSelection(): void {
        this.dialogRef.close();
    }

    public onFolderSelected(folderId: number): void {
        this.folderControlOptions = new RpcSelectControltOptions(this.form, 'folder', this.getFolderSelectOptions(), folderId);

        this.changeDetectorRef.detectChanges();

        this.onCloseFolderSelection();
    }

    public onIncludePreviousMatchesChange(state: RpcCheckboxState): void {
        this.includePreviousMatches = state;
    }

    private buildForm(): void {
        this.form = this.formBuilder.group({});
        this.createFormControls(this.savedSearch?.name, this.folderId);
    }

    private recreateFormControls(): void {
        if (this.form == null) {
            return;
        }

        const savedSearchesName = this.nameControlValue;
        const folderId = Boolean(this.newlyCreatedFolderName)
            ? this.folders.find(({ name }) => name === this.newlyCreatedFolderName).id
            : this.folderControlValue;

        this.createFormControls(savedSearchesName, folderId);

        this.newlyCreatedFolderName = null;
    }

    private createFormControls(savedSearchName: string, folderId: number): void {
        const savedSearchesNamesWithoutCurrent = this.savedSearches.reduce(
            (names, { id, name }) => this.savedSearch?.id === id ? names : [...names, name],
            Array<string>()
        );

        this.nameControlOptions = new RpcInputOptions(this.form, 'name', savedSearchName ?? '', this.getNameValidators(savedSearchesNamesWithoutCurrent));
        this.folderControlOptions = new RpcSelectControltOptions(this.form, 'folder', this.getFolderSelectOptions(), folderId ?? this.folderNotSelectedId);

        this.isDisabledByTenantAgreementPolicy$.pipe(take(1)).subscribe(isDisabledByTenantAgreementPolicy => {
            this.includePreviousMatches = isDisabledByTenantAgreementPolicy ? 'unchecked' : 'checked';
            this.enableEmail = isDisabledByTenantAgreementPolicy
                ? 'unchecked'
                : this.savedSearch == null
                    ? this.enableEmail
                    : (this.savedSearch?.enableEmail ? 'checked' : 'unchecked');
        });
    }

    private getFolderSelectOptions(): RpcSelectOption[] {
        const options = this.folders.map((folder): RpcSelectOption => ({
            text: folder.name,
            value: folder.id,
        }));

        return options;
    }

    private getNameValidators(savedSearchesNames: string[]): ValidatorsMapItem[] {
        return [
            {
                message: 'SAVED_SEARCH_FORM.VALIDATION_ERRORS.REQUIRED',
                showError: (control: UntypedFormControl) => control.hasError('required'),
                validator: Validators.required
            },
            {
                message: 'SAVED_SEARCH_FORM.VALIDATION_ERRORS.WHITESPACES',
                showError: (control: UntypedFormControl) => control.hasError('whitespaces'),
                validator: this.validationService.whitespacesValidator
            },
            {
                message: 'SAVED_SEARCH_FORM.VALIDATION_ERRORS.UNIQUE',
                showError: (control: UntypedFormControl) => control.hasError('uniqueValue'),
                validator: this.validationService.uniqueValuesValidatior(savedSearchesNames, true)
            }
        ];
    }
}
